/* eslint-disable lingui/no-unlocalized-strings */

import { cn } from "@reactive-resume/utils";
import { useMemo } from "react";

import { COACH_DIFF_COLORS } from "@/client/pages/coach/optimize/coach-theme";
import {
  useCoachOptimizationStore,
  useIsCoachOptimizing,
} from "@/client/stores/coach-optimization";
import { useResumeStore } from "@/client/stores/resume";

import {
  COACH_SKILL_ADDITIONS_SECTION_ID,
  filterSuggestionsBySection,
} from "./coach-suggestion-sections";

/**
 * "AI Suggested (n)" pill shown in a section's header while a coach session is running.
 *
 * Its job is orientation, and it matters more now that sections start collapsed: the count is the only
 * thing telling a student which sections are asking for attention, without expanding each one.
 */
export const CoachSectionBadge = ({
  sectionId,
  className,
}: {
  sectionId: string;
  className?: string;
}) => {
  const resumeId = useResumeStore((state) => state.resume.id);
  const isCoachOptimizing = useIsCoachOptimizing(resumeId);

  const suggestions = useCoachOptimizationStore((state) => state.suggestions);
  const skillAdditions = useCoachOptimizationStore((state) => state.skillAdditions);
  // `acceptedIds` is deliberately NOT subscribed. The badge used to turn from primary to success once
  // every suggestion in the section had been accepted; it is now a single fixed green, so completion
  // has no representation here and the subscription would only cost a re-render of all fifteen badges
  // on every accept. The cards themselves show their own Accepted state.

  const ids = useMemo(() => {
    const sectionIds = filterSuggestionsBySection(suggestions, sectionId).map(
      (suggestion) => suggestion.id,
    );

    if (sectionId === COACH_SKILL_ADDITIONS_SECTION_ID) {
      sectionIds.push(...skillAdditions.map((addition) => addition.id));
    }

    return sectionIds;
  }, [suggestions, skillAdditions, sectionId]);

  if (!isCoachOptimizing || ids.length === 0) return null;

  return (
    // `inline-flex items-center` with an explicit height and `leading-none`, rather than `py-1` on an
    // inline span: a 10px pill next to a `text-3xl` heading inherits the heading row's line box, and
    // vertical padding around a short line-height left the text sitting high inside the pill and the
    // pill itself riding above the title's optical centre. Fixing the height and removing the
    // line-height slack lets the header's own `items-center` align it against the heading.
    <span
      className={cn(
        "inline-flex h-6 shrink-0 items-center whitespace-nowrap rounded-full px-2.5 text-[10px] font-medium leading-none",
        className,
      )}
      // The coach green, at full strength for the text and a wash for the fill. Not the `success`
      // token: that is the app-wide semantic green, and this badge is part of the coach palette.
      style={{
        color: COACH_DIFF_COLORS.added,
        backgroundColor: `${COACH_DIFF_COLORS.added}1F`,
      }}
    >
      AI Suggested ({ids.length})
    </span>
  );
};
